import type { HTMLAttributes } from 'react';
import type { Language as LanguageType } from 'src/generated/graphql-manager';

import { CopyToClipboard, InputDescription, TextInput } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { generateSrtUrl, getLanguageKey } from '../../utils';

import styles from './srt-stream-info.module.css';

type SrtStreamInfoProps = {
  streams:
    | {
        ingest: {
          primaryServer: string;
          backupServer: string;
          password: string;
        };
        metadata?: {
          language?: string | null;
        } | null;
      }[]
    | undefined;
  languages: (LanguageType | undefined)[];
} & HTMLAttributes<HTMLDivElement>;

export function SrtStreamInfo({ streams, languages, ...props }: SrtStreamInfoProps) {
  const { t } = useTranslation();

  return streams?.map((stream) => {
    const language = languages.find((language) => language?.code === stream.metadata?.language);

    if (!language) return null;

    const languageKey =
      language.__typename === 'CustomLanguage' ? language.name : t(`common.languages.${getLanguageKey(language.code)}`);

    const primaryServerUrl = generateSrtUrl(stream.ingest.primaryServer, stream.ingest.password);
    const backupServerUrl = generateSrtUrl(stream.ingest.backupServer, stream.ingest.password);

    return (
      <div key={`srt-server-info-${language.code}`} className={styles.wrapper} {...props}>
        {streams.length > 1 && <span>{languageKey}</span>}

        {primaryServerUrl && (
          <InputDescription
            inputId={`server-url-${language.code}`}
            label={t('manager.webcastSetup.summary.encoder.streamingServer.serverUrlLabel')}
          >
            <TextInput readOnly value={primaryServerUrl} data-testid={`server-url-${language.code}`}>
              <CopyToClipboard value={primaryServerUrl} notificationPosition="left" />
            </TextInput>
          </InputDescription>
        )}

        {backupServerUrl && (
          <InputDescription
            inputId={`server-url-${language.code}-backup`}
            label={t('manager.webcastSetup.summary.encoder.streamingServer.backupServerUrlLabel')}
          >
            <TextInput readOnly value={backupServerUrl} data-testid={`server-url-${language.code}-backup`}>
              <CopyToClipboard value={backupServerUrl} notificationPosition="left" />
            </TextInput>
          </InputDescription>
        )}
      </div>
    );
  });
}
